import cn from 'classnames';
import React, { FC } from 'react';

import { Bars } from '@.partials';
import { Filter3, FilterData } from '@.ui';
import { HeartFill } from '@/Components/Icons';
import { AnalyticChartCardInfo } from '@/Components/Partials/Analytics/AnalyticChartCardInfo';
import { AnalyticChartFilterInfo } from '@/Components/Partials/Analytics/AnalyticChartFilterInfo';
import { BAR_FACTOR, MINI_TOP_STEP_VALUE, TOP_STEP_VALUE } from '@/Components/Partials/Analytics/Charts/FavoriteHeartChart/constants';
import { isNumber, loop } from '@/Lib';
import { analyticsStoreSelectors } from '@/Store';
import { Dictionary } from '@/Types';
import { ChartDatasetValues, StatisticBestProduct } from '@/Types/analytics';
import { DictionaryItem } from '@/Types/api/Dictionaries';

import S from './styles.module.scss';

type FavoriteHeartChartProps = {
    data: ChartDatasetValues;
    bestProduct?: StatisticBestProduct;
    totalProducts: number;
    totalHearts: number;
    limit: number;
    mini?: boolean;
};

export const FavoriteHeartChart: FC<FavoriteHeartChartProps> = ({ data, bestProduct, totalHearts, totalProducts, limit, mini = false }) => {
    const filterFavoriteHeartByLimit = analyticsStoreSelectors.use.filterFavoriteHeartByLimit();

    const stepValue = mini ? MINI_TOP_STEP_VALUE : TOP_STEP_VALUE;

    const onChangeFavoriteHeartTopFilter = (item: FilterData) => {
        const limit = Number(item.limit);

        if (isNumber(limit) && !isNaN(limit)) {
            filterFavoriteHeartByLimit(limit);
        }
    };

    const filterFormData = loop(1, Math.trunc(totalProducts / stepValue), (value: number): DictionaryItem => {
        return {
            name: `Top ${value * stepValue}`,
            id: value * stepValue,
        };
    });

    const filters = [
        <Filter3
            key={totalProducts}
            fromData={{
                [Dictionary.FavoriteHeart]: filterFormData,
            }}
            isSaveInQuery={false}
            onChange={onChangeFavoriteHeartTopFilter}
        >
            <Filter3.FavoriteHeart noReset visibleLabel={''} visibleName={`Top ${limit}`} />
        </Filter3>,
    ];

    const heartIcon = <HeartFill />;

    const createCardsInfo = () => {
        const totalHeartsData = {
            key: totalHearts,
            title: totalHearts.toString(),
            description: 'total hearts per period',
        };

        if (bestProduct) {
            const bestProductDescription = bestProduct ? (
                <>
                    <p>{bestProduct.name}</p>
                    <p>best product</p>
                </>
            ) : null;

            const bestProductData = {
                key: bestProduct.id,
                title: bestProduct.value.toString(),
                description: bestProductDescription,
            };

            return [bestProductData, totalHeartsData];
        }

        return [totalHeartsData];
    };

    const cardsInfoData = createCardsInfo();

    const cardsInfo = cardsInfoData.map((card, index) => (
        <AnalyticChartCardInfo key={card.key + index} title={card.title} description={card.description} icon={heartIcon} />
    ));

    const height = data.length * stepValue * BAR_FACTOR;

    return (
        <div className={cn({ [S['whiffs_chart-mini']]: mini })}>
            <h3 className={cn(S.title, { [S['title-mini']]: mini })}>Favorite Heart counts per item (per brand)</h3>

            {!mini && <AnalyticChartFilterInfo cardsInfo={cardsInfo} filters={filters} />}

            <Bars data={data} isHorizontal height={height} axisDataName="Product name" isHideFractionalTicks mini={mini} />
        </div>
    );
};
